import { FoodSource, FRUIT_VARIATIONS_PER_COUNT } from '@antification/core';
import { Camera } from './Camera';

/**
 * Pre-rendered fruit sprite. Centred, so the bounds are all a caller needs.
 */
interface CachedFruitSprite {
  canvas: HTMLCanvasElement;
  width: number;
  height: number;
}

const MAX_FRUITS = 7;    // 0-6 fruits, plus one for array indexing
const SPRITE_SIZE = 24;  // Pixels; covers the fruit spread area

/**
 * Draws fruit on bushes from pre-rendered sprites — one `drawImage` per bush
 * rather than a dozen arcs.
 *
 * This used to live inside `FoodSourceManager`, whose constructor therefore
 * called `document.createElement('canvas')` and could not be built in Node. The
 * simulation now holds only what a bush *is*; how it looks is here, on the
 * render side of the line, where a DOM is guaranteed and no ant's fate depends
 * on the outcome.
 *
 * Sprite variation stays deterministic (a positional hash of variation and
 * fruit count) so the same bush looks the same every session, but nothing here
 * feeds back into simulation state.
 */
export class FoodSourceRenderer {
  /** [fruitCount][variationIndex] -> sprite */
  private spriteCache: CachedFruitSprite[][] = [];
  private spriteCacheScale = 0;

  render(ctx: CanvasRenderingContext2D, camera: Camera, sources: readonly FoodSource[]): void {
    const scale = camera.getScale();

    // Build on first use, and rebuild when the zoom has moved more than 20%.
    if (this.spriteCacheScale <= 0
      || Math.abs(scale - this.spriteCacheScale) / this.spriteCacheScale > 0.2) {
      this.buildSpriteCache(scale);
    }

    // Handles the minor scale changes that don't warrant a rebuild.
    const drawScale = scale / this.spriteCacheScale;

    for (const source of sources) {
      const fruitCount = source.fruits.length;
      if (fruitCount <= 0) continue;

      // Fade the whole bush by its most-decayed fruit.
      let minAlpha = 1;
      for (const fruit of source.fruits) {
        const fadeStart = fruit.maxAge * 0.8;
        if (fruit.age > fadeStart) {
          const alpha = 1 - (fruit.age - fadeStart) / (fruit.maxAge - fadeStart);
          minAlpha = Math.min(minAlpha, alpha);
        }
      }

      const sprite = this.spriteCache[fruitCount]?.[source.variationIndex];
      if (!sprite) continue;

      const screen = camera.gridToScreen(source.gridX, source.gridY);
      const drawWidth = sprite.width * drawScale;
      const drawHeight = sprite.height * drawScale;
      const drawX = screen.x - drawWidth / 2;
      const drawY = screen.y - drawHeight / 2;

      if (minAlpha < 1) ctx.globalAlpha = minAlpha;
      ctx.drawImage(sprite.canvas, drawX, drawY, drawWidth, drawHeight);
      if (minAlpha < 1) ctx.globalAlpha = 1;
    }
  }

  private buildSpriteCache(scale: number): void {
    this.spriteCacheScale = scale;
    this.spriteCache = [];

    for (let count = 0; count < MAX_FRUITS; count++) {
      this.spriteCache[count] = [];
      for (let variation = 0; variation < FRUIT_VARIATIONS_PER_COUNT; variation++) {
        this.spriteCache[count][variation] = this.createFruitSprite(count, variation, scale);
      }
    }
  }

  /**
   * RENDER-ONLY randomness: sprite layout from a positional hash. Touches no
   * simulation state, so it needs no injected generator.
   */
  private spriteNoise(seed: number): number {
    let h = Math.imul(seed + 0x9e3779b9, 0x85ebca6b);
    h ^= h >>> 15;
    h = Math.imul(h, 0x2545f491);
    h ^= h >>> 13;
    return (h >>> 0) / 4294967296;
  }

  private createFruitSprite(fruitCount: number, variation: number, scale: number): CachedFruitSprite {
    const size = Math.ceil(SPRITE_SIZE * scale);
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;

    const cx = size / 2;
    const cy = size / 2;
    const baseSeed = variation * 1000 + fruitCount * 100;

    for (let i = 0; i < fruitCount; i++) {
      const seed = baseSeed + i;

      const angle = this.spriteNoise(seed) * Math.PI * 2;
      const distance = (2 + this.spriteNoise(seed + 1) * 4) * scale;
      const offsetX = Math.cos(angle) * distance;
      const offsetY = Math.sin(angle) * distance - 6 * scale; // Sit on the bush

      const hue = this.spriteNoise(seed + 2) * 40;
      const fruitSize = 0.8 + this.spriteNoise(seed + 3) * 0.4;
      const radius = 1.2 * fruitSize * scale;

      let r: number, g: number, b: number;
      if (hue < 15) {
        r = 220; g = 20 + hue * 3; b = 60;
      } else if (hue < 30) {
        r = 255; g = 100 + (hue - 15) * 6; b = 30;
      } else {
        r = 255; g = 200 + (hue - 30) * 2; b = 50;
      }

      const x = cx + offsetX;
      const y = cy + offsetY;

      ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
      ctx.beginPath();
      ctx.arc(x - radius * 0.25, y - radius * 0.25, radius * 0.3, 0, Math.PI * 2);
      ctx.fill();
    }

    return { canvas, width: size, height: size };
  }
}
